/**
 * UnifiedDocumentUploader Component
 *
 * Document upload UI that uses the facet-first analyzer pipeline.
 * Designed for use in Canvas, Discovery, and AI Coach modules.
 *
 * Features:
 * - File upload (PDF, TXT, MD)
 * - Paste text
 * - Facet-first extraction with evidence
 * - Visual analysis for PDFs
 * - Enhanced progress tracking with step counts and percentages
 */

import { useState, useRef, useCallback } from 'react'
import { useProjectStore } from '@/stores/projectStore'
import { useSessionStore } from '@/stores/sessionStore'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  FileUp,
  Loader2,
  AlertCircle,
  FileText,
  Sparkles,
  Globe,
} from 'lucide-react'
import type { ContentType, DetectedFacet, CrawlConfig } from '@/lib/api/client'
import { webSourcesApi } from '@/lib/api/client'
import { WebSourceUploader } from '@/components/sources/WebSourceUploader'
import { WebCrawlProgress } from '@/components/sources/WebCrawlProgress'
import { CONTENT_TYPES, CONTENT_TYPE_ICONS } from '@/lib/analyzer/types'
import {
  useDocumentAnalyzer,
  type AnalysisResult,
} from '@/hooks/useDocumentAnalyzer'
import { AnalysisProgressDisplay } from './AnalysisProgressDisplay'

// ============================================================================
// MAIN COMPONENT
// ============================================================================

interface UnifiedDocumentUploaderProps {
  /** Source context for tracking origin */
  sourceContext: 'canvas' | 'discovery' | 'coach'

  /** Callback when analysis is complete */
  onComplete?: (result: AnalysisResult) => void

  /** Callback when facets are extracted */
  onFacetsExtracted?: (facets: DetectedFacet[]) => void

  /** Callback on error */
  onError?: (error: Error) => void

  /** Close handler (for modal usage) */
  onClose?: () => void

  /** Default content type */
  defaultContentType?: ContentType

  /** Compact mode for embedded usage */
  compact?: boolean

  className?: string
}

export function UnifiedDocumentUploader({
  sourceContext,
  onComplete,
  onFacetsExtracted,
  onError,
  onClose,
  defaultContentType = 'custom',
  compact = false,
  className = '',
}: UnifiedDocumentUploaderProps) {
  const { currentProject } = useProjectStore()
  const { accountId, projectId } = useSessionStore()
  const visualAnalysisEnabled = currentProject?.visualAnalysisEnabled ?? false
  const [activeTab, setActiveTab] = useState<'paste' | 'upload' | 'web'>('paste')

  // Web source state
  const [activeWebSourceId, setActiveWebSourceId] = useState<string | null>(null)
  const [webSourceLoading, setWebSourceLoading] = useState(false)
  const [webSourceError, setWebSourceError] = useState<string | null>(null)
  const [title, setTitle] = useState('')
  const [content, setContent] = useState('')
  const [contentType, setContentType] = useState<ContentType>(defaultContentType)
  const [file, setFile] = useState<File | null>(null)
  const [localError, setLocalError] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const {
    progress,
    isAnalyzing,
    isConfigured,
    error,
    analyzeDocument,
    reset,
    clearError,
  } = useDocumentAnalyzer({
    onFacetsExtracted: (textFacets, visualFacets) => {
      onFacetsExtracted?.([...textFacets, ...visualFacets])
    },
    onAnalysisComplete: onComplete,
    onError: (err) => {
      setLocalError(err.message)
      onError?.(err)
    },
  })

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0]
    if (!selectedFile) return

    setFile(selectedFile)
    setLocalError(null)

    // Auto-set title from filename
    if (!title) {
      setTitle(selectedFile.name.replace(/\.[^.]+$/, ''))
    }

    // Preview content for non-PDF files
    if (!selectedFile.name.toLowerCase().endsWith('.pdf')) {
      try {
        const text = await selectedFile.text()
        setContent(text)
      } catch (err) {
        console.warn('Failed to preview file:', err)
      }
    } else {
      setContent('') // Clear preview for PDFs (extracted during analysis)
    }
  }

  const handleSubmit = async () => {
    if (!isConfigured) {
      setLocalError('Claude API key not configured. Please set it in Settings.')
      return
    }

    if (!title.trim()) {
      setLocalError('Title is required')
      return
    }

    const hasContent = activeTab === 'upload' ? !!file : content.trim().length >= 50

    if (!hasContent) {
      setLocalError(activeTab === 'upload' ? 'Please select a file' : 'Content must be at least 50 characters')
      return
    }

    setLocalError(null)
    clearError()

    const result = await analyzeDocument({
      file: activeTab === 'upload' ? file! : undefined,
      content: activeTab === 'paste' ? content : undefined,
      title: title.trim(),
      contentType,
      sourceContext,
    })

    if (result) {
      // Reset form on success
      setTitle('')
      setContent('')
      setFile(null)
      setActiveTab('paste')
    }
  }

  const handleReset = () => {
    reset()
    setLocalError(null)
    setTitle('')
    setContent('')
    setFile(null)
    setActiveWebSourceId(null)
    setWebSourceError(null)
  }

  // Handle web source submission
  const handleWebSourceSubmit = useCallback(
    async (url: string, crawlConfig?: CrawlConfig) => {
      if (!accountId || !projectId) {
        setWebSourceError('Account or project not selected')
        return
      }

      setWebSourceLoading(true)
      setWebSourceError(null)

      try {
        const result = await webSourcesApi.scrape(accountId, projectId, { url, crawlConfig })
        if (result.webSourceId) {
          setActiveWebSourceId(result.webSourceId)
        }
      } catch (err) {
        setWebSourceError(err instanceof Error ? err.message : 'Failed to start web scrape')
      } finally {
        setWebSourceLoading(false)
      }
    },
    [accountId, projectId]
  )

  // Handle web crawl completion
  const handleWebCrawlComplete = useCallback(
    (_sourceId: string | null) => {
      // Web source creates its own source record - reset UI state
      // Parent component can poll for new sources or refresh
      setActiveWebSourceId(null)
      setWebSourceError(null)
    },
    []
  )

  const displayError = localError || error?.message

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Title */}
      <div className="space-y-2">
        <Label htmlFor="title" className={compact ? 'text-xs' : ''}>
          Title
        </Label>
        <Input
          id="title"
          placeholder="e.g., Q4 Strategy Document"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          disabled={isAnalyzing}
          className={compact ? 'h-8 text-sm' : ''}
        />
      </div>

      {/* Content Type */}
      <div className="space-y-2">
        <Label htmlFor="contentType" className={compact ? 'text-xs' : ''}>
          Content Type
        </Label>
        <Select
          value={contentType}
          onValueChange={(v) => setContentType(v as ContentType)}
          disabled={isAnalyzing}
        >
          <SelectTrigger className={compact ? 'h-8 text-sm' : ''}>
            <SelectValue placeholder="Select content type" />
          </SelectTrigger>
          <SelectContent>
            {CONTENT_TYPES.map((ct) => {
              const IconComponent = CONTENT_TYPE_ICONS[ct.icon]
              return (
                <SelectItem key={ct.id} value={ct.id}>
                  <span className="flex items-center gap-2">
                    {IconComponent && <IconComponent className="h-4 w-4 text-zinc-500" />}
                    <span>{ct.name}</span>
                  </span>
                </SelectItem>
              )
            })}
          </SelectContent>
        </Select>
      </div>

      {/* Content Input */}
      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'paste' | 'upload' | 'web')}>
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="paste" disabled={isAnalyzing || !!activeWebSourceId}>
            Paste Text
          </TabsTrigger>
          <TabsTrigger value="upload" disabled={isAnalyzing || !!activeWebSourceId}>
            Upload File
          </TabsTrigger>
          <TabsTrigger value="web" disabled={isAnalyzing}>
            <Globe className="h-3 w-3 mr-1" />
            From URL
          </TabsTrigger>
        </TabsList>

        <TabsContent value="paste" className="space-y-2">
          <Label htmlFor="content" className={compact ? 'text-xs' : ''}>
            Content
          </Label>
          <Textarea
            id="content"
            placeholder="Paste your document content here..."
            value={content}
            onChange={(e) => setContent(e.target.value)}
            rows={compact ? 8 : 12}
            className="font-mono text-sm"
            disabled={isAnalyzing}
          />
          <p className="text-xs text-text-tertiary">
            {content.length} characters
            {content.length > 0 && content.length < 50 && (
              <span className="text-red-500"> (minimum 50)</span>
            )}
          </p>
        </TabsContent>

        <TabsContent value="upload" className="space-y-4">
          <div
            className={`border-2 border-dashed border-warm-200 rounded-lg ${
              compact ? 'p-4' : 'p-8'
            } text-center transition-colors ${
              isAnalyzing ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:border-warm-400'
            }`}
            onClick={() => !isAnalyzing && fileInputRef.current?.click()}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".txt,.md,.markdown,.pdf"
              onChange={handleFileSelect}
              className="hidden"
              disabled={isAnalyzing}
            />
            {file ? (
              <div>
                <FileUp className={`mx-auto mb-2 text-solution ${compact ? 'h-6 w-6' : 'h-8 w-8'}`} />
                <p className="font-medium">{file.name}</p>
                <p className="text-sm text-text-secondary">
                  {(file.size / 1024).toFixed(1)} KB
                  {file.name.toLowerCase().endsWith('.pdf') && visualAnalysisEnabled && (
                    <span className="ml-2 text-solution">
                      <Sparkles className="inline h-3 w-3 mr-0.5" />
                      PDF with visual analysis
                    </span>
                  )}
                </p>
              </div>
            ) : (
              <div>
                <FileUp className={`mx-auto mb-2 text-warm-400 ${compact ? 'h-6 w-6' : 'h-8 w-8'}`} />
                <p className="text-text-secondary">
                  {compact ? 'Click to upload' : 'Click to select a file or drag and drop'}
                </p>
                <p className="text-xs text-text-tertiary mt-1">
                  PDF, TXT, MD - AI will extract insights
                </p>
              </div>
            )}
          </div>

          {/* Preview for non-PDF files */}
          {content && !file?.name.toLowerCase().endsWith('.pdf') && (
            <div className="space-y-2">
              <Label className="text-xs">Preview</Label>
              <div className="border rounded-lg p-3 max-h-32 overflow-y-auto bg-warm-50">
                <pre className="text-xs whitespace-pre-wrap font-mono">
                  {content.slice(0, 500)}
                  {content.length > 500 && '...'}
                </pre>
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="web" className="space-y-4">
          {activeWebSourceId && accountId && projectId ? (
            <WebCrawlProgress
              webSourceId={activeWebSourceId}
              accountId={accountId}
              projectId={projectId}
              onComplete={handleWebCrawlComplete}
            />
          ) : (
            <WebSourceUploader
              onSubmit={handleWebSourceSubmit}
              isLoading={webSourceLoading}
              error={webSourceError}
              disabled={isAnalyzing}
            />
          )}
        </TabsContent>
      </Tabs>

      {/* Progress Display */}
      <AnalysisProgressDisplay progress={progress} />

      {/* Error Message */}
      {displayError && progress.stage !== 'error' && (
        <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
          <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-red-700">{displayError}</div>
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center justify-end gap-2 pt-2">
        {onClose && (
          <Button variant="outline" onClick={onClose} disabled={isAnalyzing} size={compact ? 'sm' : 'default'}>
            Cancel
          </Button>
        )}
        {progress.stage === 'complete' || progress.stage === 'error' ? (
          <Button onClick={handleReset} variant="outline" size={compact ? 'sm' : 'default'}>
            Upload Another
          </Button>
        ) : (
          <Button
            onClick={handleSubmit}
            disabled={isAnalyzing || !title.trim() || (activeTab === 'paste' ? content.length < 50 : !file)}
            className="bg-solution hover:bg-solution/90"
            size={compact ? 'sm' : 'default'}
          >
            {isAnalyzing ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Analyzing...
              </>
            ) : (
              <>
                <Sparkles className="h-4 w-4 mr-2" />
                Upload & Extract
              </>
            )}
          </Button>
        )}
      </div>
    </div>
  )
}

// ============================================================================
// FACET LIST COMPONENT (for displaying results)
// ============================================================================

interface FacetListProps {
  facets: DetectedFacet[]
  maxDisplay?: number
  showEvidence?: boolean
  onFacetClick?: (facet: DetectedFacet) => void
}

export function FacetList({
  facets,
  maxDisplay = 5,
  showEvidence = false,
  onFacetClick,
}: FacetListProps) {
  const displayFacets = facets.slice(0, maxDisplay)
  const remainingCount = facets.length - maxDisplay

  if (facets.length === 0) {
    return (
      <div className="text-center py-4 text-text-tertiary text-sm">
        No facets extracted yet
      </div>
    )
  }

  return (
    <div className="space-y-2">
      {displayFacets.map((facet) => (
        <div
          key={facet.id}
          className={`p-3 bg-white border border-warm-200 rounded-lg ${
            onFacetClick ? 'cursor-pointer hover:border-solution' : ''
          }`}
          onClick={() => onFacetClick?.(facet)}
        >
          <div className="flex items-start gap-2">
            <FileText className="h-4 w-4 text-solution flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-text-primary line-clamp-2">
                {facet.content}
              </p>
              {showEvidence && facet.evidence && (
                <p className="text-xs text-text-secondary mt-1 line-clamp-2 italic">
                  "{facet.evidence}"
                </p>
              )}
              <div className="flex items-center gap-2 mt-1">
                <span className="text-xs px-1.5 py-0.5 bg-warm-100 rounded text-text-secondary">
                  {facet.facetTypeId}
                </span>
                <span className="text-xs text-text-tertiary">
                  {Math.round(facet.confidence * 100)}% confidence
                </span>
              </div>
            </div>
          </div>
        </div>
      ))}

      {remainingCount > 0 && (
        <div className="text-xs text-center text-text-tertiary py-2">
          +{remainingCount} more facets
        </div>
      )}
    </div>
  )
}
